import { Injectable, inject } from '@angular/core';
import { CompteEntreprise, Contact, Invitation, CompanyMscompany, Users, CompanyDetails } from '../models/compte-entreprise.model';
import { HttpClient, HttpParams } from '@angular/common/http';
import { DetailsEntreprise } from '../models/details-entreprise.model';
import { environment } from '@env/environment';
import { catchError, Observable, of, take, tap } from 'rxjs';
import { GS1NotificationService } from '@gs1france/gs1-components-library';
import { TranslateService } from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
export class CompteEntrepriseService {
  __URL_API__ = `${environment.URL_API}`;

  private _http = inject(HttpClient);
  private notificationService = inject(GS1NotificationService);
  private translate = inject(TranslateService);

  getCompteEntreprise(): Observable<CompteEntreprise> {
    return this._http.get<CompteEntreprise>(this.__URL_API__ + 'api/Entreprise/GetCompteByNum').pipe(
      catchError((error) => {
        console.error(error);
        return of(null);
      })
    );
  }

  getMSCompanyEntreprise(accountNumber: string): Observable<CompanyMscompany> {
    return this._http.get<CompanyMscompany>(this.__URL_API__ + 'api/Entreprise/GetMsCompanyEntreprise?accountNumber=' + accountNumber).pipe(
      catchError((error) => {
        console.error(error);
        return of(null);
      })
    );
  }

  getDtailsEntreprise(siren: string): Observable<DetailsEntreprise[]> {
    return this._http.get<DetailsEntreprise[]>(this.__URL_API__ + 'api/Entreprise/GetDetailEntreprise?siren=' + siren).pipe(
      catchError((error) => {
        console.error(error);
        return of(null);
      })
    );
  }

  getCompanyDetails(): Observable<CompanyDetails> {
    return this._http.get<CompanyDetails>(this.__URL_API__ + 'api/companies').pipe(
      take(1),
      catchError((err) => {
        console.error(err);
        return of(null);
      })
    );
  }

  updateCompteEntreprise(compteEntrepriseForm: CompteEntreprise): Observable<string> {
    const formData: FormData = new FormData();
    formData.append('nom', compteEntrepriseForm.nom == undefined ? '' : compteEntrepriseForm.nom.toString());
    formData.append('adresse', compteEntrepriseForm.adresse == undefined ? '' : compteEntrepriseForm.adresse.toString());
    formData.append('adresseComp', compteEntrepriseForm.adresseComp == undefined ? '' : compteEntrepriseForm.adresseComp.toString());
    formData.append('codePostale', compteEntrepriseForm.codePostale == undefined ? '' : compteEntrepriseForm.codePostale.toString());
    formData.append('ville', compteEntrepriseForm.ville == undefined ? '' : compteEntrepriseForm.ville.toString());
    formData.append('pays', compteEntrepriseForm.pays == undefined ? '' : compteEntrepriseForm.pays.toString());
    formData.append('telephone', compteEntrepriseForm.telephone == undefined ? '' : compteEntrepriseForm.telephone.toString());
    formData.append('siren', compteEntrepriseForm.siren == undefined ? '' : compteEntrepriseForm.siren.toString());

    return this._http.put<string>(this.__URL_API__ + 'api/Entreprise/Update', formData).pipe(
      take(1),
      catchError((error) => {
        console.error(error);
        return of(null);
      })
    );
  }

  updateMscompany(company: CompanyMscompany): Observable<void> {
    return this._http.patch<void>(this.__URL_API__ + 'api/Entreprise/UpdateMsCompany', company).pipe(
      take(1),
      catchError((error) => {
        console.error(error);
        return of(null);
      })
    );
  }

  updateCompanyDetails(company: CompanyDetails, isFromActivity: boolean = true): Observable<CompanyDetails> {
    return this._http.patch<CompanyDetails>(this.__URL_API__ + 'api/companies', company, { headers: { 'Content-Type': 'application/json' } }).pipe(
      take(1),
      tap(() =>
        this.notificationService.addNotification(this.translate.instant(isFromActivity ? 'Company.Activity.updateSuccess' : 'Company.Identity.Notification.success'), 'success')
      ),
      catchError((err) => {
        this.notificationService.addNotification(this.translate.instant(isFromActivity ? 'Company.Activity.updateError' : 'Company.Identity.Notification.error'), 'error');
        console.error(err);
        return of(null);
      })
    );
  }

  retrieveContacts(): Observable<Contact[]> {
    return this._http.get<Contact[]>(this.__URL_API__ + 'api/Entreprise/ListeContacts').pipe(
      take(1),
      catchError((error) => {
        console.error(error);
        return of(null);
      })
    );
  }

  retrieveInvitations(): Observable<Invitation[]> {
    return this._http.get<Invitation[]>(this.__URL_API__ + 'api/Entreprise/ListeInvitations').pipe(
      take(1),
      catchError((error) => {
        console.error(error);
        return of(null);
      })
    );
  }

  getUsers(): Observable<Users> {
    return this._http.get<Users>(this.__URL_API__ + 'api/User').pipe(
      take(1),
      catchError((error) => {
        this.notificationService.addNotification(this.translate.instant('Users.load-error'), 'error');
        console.error(error);
        return of(null);
      })
    );
  }

  getSpecialUsers(): Observable<{ count: number; invitations: Invitation[] }> {
    return this._http.get<{ count: number; invitations: Invitation[] }>(this.__URL_API__ + 'api/User?isMain=true&isBilling=true').pipe(
      take(1),
      catchError((error) => {
        this.notificationService.addNotification(this.translate.instant('Users.load-error'), 'error');
        console.error(error);
        return of(null);
      })
    );
  }

  deleteUser(id: string, idContact: string, name: string): Observable<any> {
    const params = new HttpParams().set('idInvit', id).set('idContact', idContact);
    return this._http
      .patch<{
        count: number;
        invitations: Invitation[];
      }>(this.__URL_API__ + 'api/User/delete', null, { params })
      .pipe(
        take(1),
        tap(() => this.notificationService.addNotification(this.translate.instant('Users.Delete.success', { name }), 'success', 15000)),
        tap(() => idContact),
        catchError((error) => {
          this.notificationService.addNotification(this.translate.instant('Users.load-error'), 'error');
          console.error(error);
          return of(null);
        })
      );
  }

  deleteInvit(id: string, name: string): Observable<any> {
    const url = `${this.__URL_API__}api/User/cancelInvit/${id}`;
    return this._http.patch(url, null).pipe(
      take(1),
      tap(() => this.notificationService.addNotification(this.translate.instant('Users.Delete.success', { name }), 'success', 15000)),
      tap(() => id),
      catchError((error) => {
        this.notificationService.addNotification(this.translate.instant('Users.load-error'), 'error');
        console.error(error);
        return of(null);
      })
    );
  }
}
